import { useCallback, useMemo } from "react";
import { useNavigate, useParams, generatePath } from "react-router-dom";
import { useFormikContext } from "formik";
import { ButtonProps, Stack, Button } from "@mui/material";
import { useAccessControl } from "@/features/access-control";
import ButtonGroup from "@/components/ButtonGroup";
import { useDeleteMenuMutation, useDuplicateMenuMutation } from "@/features/organizations/api/menusApi";
import useSnackbar from "@/hooks/useSnackbar";
import useOrganizations from "@/features/organizations/useOrganizations";
import routes from "@/constants/routes";
import { Menu } from "@/types/menus";

interface Props {
  isLoading?: boolean;
  selectedMenu?: Menu;
  onClose: () => void;
}

const UpdateMenuActions = ({ selectedMenu, onClose, isLoading }: Props) => {
  const navigate = useNavigate();
  const [openSnackbar] = useSnackbar();
  const { hasAccess } = useAccessControl();
  const { organizationId } = useParams<{ organizationId: string }>();
  const { currentOrganization } = useOrganizations();
  const { values } = useFormikContext<Menu>();
  const [deleteMenu, deleteMenuMutation] = useDeleteMenuMutation();
  const [duplicateMenu, duplicateMenuMutation] = useDuplicateMenuMutation();

  const handleMenuDeletion = useCallback(async () => {
    if (!values.id) return;

    const response = await deleteMenu({ menuId: values.id, organizationId });

    if (response?.error) {
      openSnackbar("Error al eliminar el menú", "error");
    } else {
      openSnackbar("Menú eliminado", "success");
      onClose();
    }
  }, [deleteMenu, onClose, openSnackbar, organizationId, values.id]);

  const handleDuplicateMenu = useCallback(async () => {
    const response = await duplicateMenu({
      menu: {
        ...values,
        title: values.title.trim() + " (copia)",
      },
      organizationId,
    });

    if (response?.error) {
      openSnackbar("Error al crear una copia del menú", "error");
    } else {
      openSnackbar("Copia realizada", "success");
      onClose();
    }
  }, [duplicateMenu, onClose, openSnackbar, organizationId, values]);

  const openEditor = useCallback(() => {
    if (!selectedMenu?.id) return;

    if (!selectedMenu.flow || selectedMenu.flow.length === 0) {
      navigate(generatePath(routes.organizations.catalogo.editor, { organizationId: organizationId ?? "", menuId: selectedMenu.id }));
    } else {
      navigate(routes.organizations.menus.edit.replace(":organizationId", organizationId ?? "").replace(":menuId", selectedMenu.id));
    }
  }, [navigate, organizationId, selectedMenu]);

  const actions = useMemo(() => {
    const draft = [];

    if (hasAccess("update.menu")) {
      draft.push({
        type: "submit",
        color: "success",
        children: "Guardar",
        disabled: isLoading,
      });
    }

    if (hasAccess("duplicate.menu")) {
      draft.push({
        children: "Duplicar",
        onClick: () => handleDuplicateMenu(),
        disabled: isLoading || duplicateMenuMutation.isLoading,
      });
    }

    if (hasAccess("delete.menu")) {
      draft.push({
        color: "error",
        children: "Eliminar",
        onClick: () => handleMenuDeletion(),
        disabled: isLoading || deleteMenuMutation.isLoading,
      });
    }

    if (hasAccess("design.menu")) {
      draft.push({
        children: "Diseñar",
        onClick: openEditor,
      });
    }

    draft.push({
      children: "Previsualizar",
      onClick: () => {
        if (!selectedMenu?.id) return;

        window.open(routes.organizations.menu.read.replace(":organizationAlias", currentOrganization.alias ?? "").replace(":menuId", selectedMenu.id), "_blank");
      },
    });

    return draft as ButtonProps[];
  }, [
    currentOrganization.alias,
    deleteMenuMutation.isLoading,
    duplicateMenuMutation.isLoading,
    handleDuplicateMenu,
    handleMenuDeletion,
    hasAccess,
    isLoading,
    openEditor,
    selectedMenu?.id,
  ]);

  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <ButtonGroup actions={actions} />
      <Button disabled={isLoading} variant="text" onClick={onClose}>
        Cancelar
      </Button>
    </Stack>
  );
};

export default UpdateMenuActions;
